import { useEffect, useState } from 'react';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import {
  DateInput,
  Drawer,
  DrawerForm,
  DrawerFormActions,
  FormField,
  NumberInput,
  Textarea,
  toast,
} from '../../components/index.js';
import { pickCoveringTimesheet } from './timeHelpers.js';

function defaultEntryDate() {
  return new Date().toISOString().slice(0, 10);
}

export function TimeEntryDrawer({
  open,
  onClose,
  targetEntityType,
  targetEntityId,
  targetTitle,
  projectId = null,
  workItemId = null,
  category = 'delivery',
  onSaved,
}) {
  const { user } = useCurrentUser();
  const timesheets = useApi('/timesheets');
  const mutation = useMutation();
  const [date, setDate] = useState(defaultEntryDate());
  const [hours, setHours] = useState(1);
  const [notes, setNotes] = useState('');
  // The sheet that the save will actually write to for the currently selected date: the fetched
  // timesheet whose period covers `date`. Derived per-render from `date` so the legend and the
  // save destination always agree and both update when the user changes the date (F-G04-001).
  const coveringTimesheet = pickCoveringTimesheet(timesheets.data?.data || [], date, user?.id);

  useEffect(() => {
    if (!open) return;
    setDate(defaultEntryDate());
    setHours(1);
    setNotes('');
  }, [open, targetEntityId]);

  async function handleSubmit() {
    const payload = {
      date,
      hours: Number(hours || 0),
      notes: notes.trim(),
      targetEntityType,
      targetEntityId,
      projectId,
      workItemId,
      category,
    };
    try {
      // Create-then-continue: reuse the fetched weekly timesheet that actually covers the entry
      // date (any covering sheet, not only the "current" pick); otherwise start the week anchored
      // on the entry date (the server is find-or-create, so this returns the right week and never
      // a stale prior-week draft). Both calls run in one mutation so the submit button stays
      // disabled across them.
      const reusableId = coveringTimesheet ? coveringTimesheet.id : null;
      const saved = await mutation.run(async () => {
        let timesheetId = reusableId;
        if (!timesheetId) {
          const created = await api.post('/timesheets', { anchorDate: date });
          timesheetId = created?.data?.id || created?.data?.data?.id;
        }
        const response = await api.post('/time-entries', { ...payload, timesheetId });
        return response?.data?.data || response?.data || response || null;
      });
      timesheets.refresh?.();
      toast.success({
        title: 'Time entry saved',
        body: targetTitle || targetEntityId,
      });
      onSaved?.(saved);
      onClose?.();
    } catch (error) {
      toast.error({
        title: 'Could not save time entry',
        body: error.message,
      });
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Log time" width={520}>
      <DrawerForm onSubmit={handleSubmit} submitting={mutation.running}>
        <div className="action-drawer-note">
          <div className="eyebrow">Target</div>
          <strong>{targetTitle || targetEntityId || 'Unknown target'}</strong>
          <p>
            {targetEntityType === 'action'
              ? 'Capture collaboration and execution time without leaving the action workspace.'
              : 'Capture delivery effort without leaving the work-item inspector.'}
          </p>
          <p className="muted">
            {coveringTimesheet
              ? `Weekly timesheet: ${coveringTimesheet.id}`
              : 'A weekly timesheet will be started automatically for this date.'}
          </p>
        </div>

        <FormField label="Date" htmlFor="time-entry-date" required>
          <DateInput id="time-entry-date" value={date} onChange={setDate} />
        </FormField>

        <FormField label="Hours" htmlFor="time-entry-hours" required>
          <NumberInput
            id="time-entry-hours"
            value={hours}
            onChange={setHours}
            min={0.25}
            step={0.25}
          />
        </FormField>

        <FormField
          label="Notes"
          htmlFor="time-entry-notes"
          hint="Summarize the work performed or coordination completed."
        >
          <Textarea
            id="time-entry-notes"
            rows={3}
            value={notes}
            onChange={setNotes}
            placeholder="Describe the delivery or action effort completed."
          />
        </FormField>

        <DrawerFormActions
          onCancel={onClose}
          submitLabel="Save time entry"
          submitting={mutation.running}
        />
      </DrawerForm>
    </Drawer>
  );
}
